import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getCheckListByid, deleteChecklistItem, updateChecklistItem } from "../../../services/api/qa/checklistService";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import { toast } from "react-hot-toast";

function ChecklistView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [checklist, setChecklist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editItem, setEditItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    useEffect(() => {
        fetchChecklist();
    }, []);

    const fetchChecklist = async () => {
        try {
            const response = await getCheckListByid(id);
            setChecklist(response.data);
        } catch (err) {
            console.error(err);
            setError("Failed to load checklist");
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="p-6">Loading checklist...</div>;
    }

    if (error) {
        return <div className="p-6 text-red-500">{error}</div>;
    }

    if (!checklist) {
        return <div className="p-6">No checklist found.</div>;
    }

    const confirmDelete = async () => {
        try {
            await deleteChecklistItem(selectedId);

            setChecklist(prev => ({
                ...prev,
                items: prev.items.filter(item => item.id !== selectedId)
            }));

            toast.success("Item deleted successfully");
        } catch (error) {
            toast.error("Failed to delete item");
        } finally {
            setShowDeleteModal(false);
            setSelectedId(null);
        }
    };

    const handleUpdate = async () => {
        try {
            await updateChecklistItem(editItem.id, editItem);
            setShowModal(false);
            fetchChecklist();
        } catch (error) {
            alert("Failed to update item");
        }
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-semibold">
                        {checklist.name}
                    </h2>
                    <p className="text-sm text-gray-500">
                        Checklist ID: {checklist.id}
                    </p>
                </div>

                <span
                    className={`px-3 py-1 text-sm rounded ${checklist.status === "Active"
                        ? "bg-green-100 text-green-600"
                        : "bg-red-100 text-red-600"
                        }`}
                >
                    {checklist.status}
                </span>
            </div>

            {/* Info Section */}
            <div className="bg-white shadow rounded p-6 mb-6">
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-gray-500">Project</p>
                        <p className="font-medium">
                            {checklist.project_name || "-"}
                        </p>
                    </div>

                    <div>
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="font-medium">
                            {checklist.description || "-"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Items Table */}
            <div className="bg-white shadow rounded p-6">
                <h3 className="text-lg font-semibold mb-4">
                    Checklist Items
                </h3>

                {checklist.items && checklist.items.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full border text-sm">
                            <thead className="bg-gray-100">
                                <tr>
                                    <th className="border px-4 py-2 text-left">#</th>
                                    <th className="border px-4 py-2 text-left">
                                        Check Point
                                    </th>
                                    <th className="border px-4 py-2 text-left">
                                        Type
                                    </th>
                                    <th className="border px-4 py-2 text-left">
                                        Required
                                    </th>
                                    <th className="border px-4 py-2">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checklist.items.map((item, index) => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="border px-4 py-2">
                                            {index + 1}
                                        </td>
                                        <td className="border px-4 py-2">
                                            {item.check_point}
                                        </td>
                                        <td className="border px-4 py-2 capitalize">
                                            {item.type.replace("_", " ")}
                                        </td>
                                        <td className="border px-4 py-2">
                                            {item.is_required ? (
                                                <span className="text-green-600 font-medium">
                                                    Yes
                                                </span>
                                            ) : (
                                                <span className="text-gray-500">
                                                    No
                                                </span>
                                            )}
                                        </td>

                                        <td className="border px-4 py-2 space-x-2">
                                            <button
                                                onClick={() => {
                                                    setEditItem(item);
                                                    setShowModal(true);
                                                }}
                                                className="text-blue-600"
                                            >
                                                <FiEdit size={18} />
                                            </button>

                                            <button
                                                onClick={() => {
                                                    setSelectedId(item.id);
                                                    setShowDeleteModal(true);
                                                }}
                                                className="text-red-600"
                                            >
                                                <FiTrash2 size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-gray-500">
                        No checklist items found.
                    </p>
                )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
                <button
                    onClick={() => navigate(-1)}
                    className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                    Back
                </button>

                <button
                    onClick={() =>
                        navigate(`/checklists/${id}/items`)
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Add Items
                </button>
            </div>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <div className="bg-white p-6 rounded shadow w-96">
                        <h3 className="text-lg font-semibold mb-4">
                            Edit Checklist Item
                        </h3>

                        <div className="mb-3">
                            <label className="text-sm">Check Point</label>
                            <input
                                type="text"
                                className="border w-full p-2 rounded"
                                value={editItem.check_point}
                                onChange={(e) =>
                                    setEditItem({
                                        ...editItem,
                                        check_point: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="mb-3">
                            <label className="text-sm">Type</label>
                            <select
                                className="border w-full p-2 rounded"
                                value={editItem.type}
                                onChange={(e) =>
                                    setEditItem({
                                        ...editItem,
                                        type: e.target.value,
                                    })
                                }
                            >
                                <option value="pass_fail">Pass / Fail</option>
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2 mb-4">
                            <input
                                type="checkbox"
                                checked={editItem.is_required}
                                onChange={(e) =>
                                    setEditItem({
                                        ...editItem,
                                        is_required: e.target.checked,
                                    })
                                }
                            />
                            <label>Is Required</label>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setShowModal(false)}
                                className="border px-3 py-1 rounded"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleUpdate}
                                className="bg-blue-600 text-white px-3 py-1 rounded"
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
                    <div className="bg-white rounded-lg shadow-lg w-96 p-6">
                        <h3 className="text-lg font-semibold mb-4">
                            Confirm Delete
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Are you sure you want to delete this checklist item?
                            This action cannot be undone.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                className="px-4 py-2 border rounded hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={confirmDelete}
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ChecklistView;